import React, { useState } from "react";
import { Organization } from "@common/types/organization";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { useActiveWorkspaceRole } from "@hooks/permissions/useActiveWorkspaceRole";
import { useActiveWorkspaceDetails } from "@hooks/useActiveWorkspaceDetails";
import { workspaceOwnsBilling } from "@helpers/billingOwnership";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useRemoveOrganizationFromWorkspaceMutation } from "@api/workspaceMember.api";

interface Props {
    organization: Organization;
}

export const OrganizationActions: React.FC<Props> = ({ organization }) => {
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const activeWorkspaceDetails = useActiveWorkspaceDetails();
    const role = useActiveWorkspaceRole();
    const { error: errorToast } = useToaster();
    const removeOrganization =
        useRemoveOrganizationFromWorkspaceMutation(organization);

    const canRemove =
        role === UserWorkspaceRole.PRIMARY_ADMIN &&
        activeWorkspaceDetails &&
        workspaceOwnsBilling(activeWorkspaceDetails);

    if (!canRemove) {
        return null;
    }

    return (
        <>
            <Menu.More>
                <Menu.Item
                    destructive
                    onClick={() => setShowDeleteConfirmation(true)}
                >
                    Remove organization from workspace
                </Menu.Item>
            </Menu.More>

            <ConfirmationModal
                show={showDeleteConfirmation}
                title={`Are you sure you want to remove ${organization.name} from the workspace?`}
                question="All members from this organization will be removed from the workspace."
                onConfirm={async () => {
                    try {
                        await removeOrganization.mutateAsync();
                        setShowDeleteConfirmation(false);
                    } catch {
                        errorToast(
                            "Failed to remove organization from workspace",
                        );
                    }
                }}
                no="Cancel"
                yes="Remove"
                onReject={() => setShowDeleteConfirmation(false)}
            />
        </>
    );
};
